import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    Row,
    Col,
    Badge,
    Button,
    Modal,
    Table,
    Spinner,
    Alert,
    Form
} from 'react-bootstrap';
import { FaEye, FaShoppingBag, FaCalendarAlt, FaMapMarkerAlt, FaPhone, FaDollarSign, FaBox } from 'react-icons/fa';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const OrderHistory = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [selectedImageAlt, setSelectedImageAlt] = useState('');

    useEffect(() => {
        fetchUserOrders();
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);

        const timer = setTimeout(() => {
            const formContainer = document.querySelector('.delivery-form') ||
                document.querySelector('form') ||
                document.querySelector('.mt-top-cartpage') ||
                document.querySelector('.cart-items-sidebar');

            if (formContainer) {
                formContainer.scrollTop = 0;

                if (window.location.pathname.includes('checkout')) {
                    const firstInput = document.querySelector('input, select, textarea');
                    if (firstInput) {
                        setTimeout(() => firstInput.focus(), 100);
                    }
                }
            }
        }, 50);

        return () => clearTimeout(timer);
    }, []);

    const fetchUserOrders = async () => {
        try {
            setLoading(true);

            const cacheKey = "cachedUserOrders";
            const cacheTimestampKey = "cachedUserOrdersTimestamp";
            const token = localStorage.getItem('token');

            if (!token) {
                setError('Please login to view your orders');
                setLoading(false);
                return;
            }

            const cachedOrders = localStorage.getItem(cacheKey);
            if (cachedOrders) {
                const parsed = JSON.parse(cachedOrders);
                setOrders(parsed);
                setError(null);
                setLoading(false);
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/orders/user/orders`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    withCredentials: false
                }
            );

            if (response.data && response.data.orders) {
                setOrders(response.data.orders);
                setError(null);
                localStorage.setItem(cacheKey, JSON.stringify(response.data.orders));
                localStorage.setItem(cacheTimestampKey, Date.now().toString());
            } else {
                setError('No orders found');
                setOrders([]);
            }
        } catch (err) {
            console.error('Error fetching orders:', err);
            if (err.response?.status === 401) {
                setError('Please login to view your orders');
            } else {
                setError('Unable to load your orders. Please try again later.');
            }
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const translateDeliveryMethod = (method) => {
        const deliveryMethodMap = {
            "standard": "Cash on Delivery",
            "vtb": "Vireak Buntham",
            "j&t": "J&T Express"
        };
        return deliveryMethodMap[method] || method;
    };

    const getStatusVariant = (status) => {
        switch (status) {
            case 'Shipped': return 'success';
            case 'Pending': return 'warning';
            default: return 'secondary';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'Shipped': return t('On Delivery');
            case 'Pending': return t('Pending');
            default: return t(status);
        }
    };

    const provinceTranslations = [
        "banteay_meanchey", "battambang", "kampong_cham", "kampong_chhnang",
        "kampong_speu", "kampong_thom", "kampot", "kandal", "koh_kong",
        "kratie", "mondulkiri", "phnom_penh", "preah_vihear", "prey_veng",
        "pursat", "siem_reap", "sihanoukville", "stung_treng", "svay_rieng",
        "takeo", "oddar_meanchey", "ratanakiri", "tboung_khmum"
    ];

    const formatProvince = (provinceKey) => {
        if (!provinceKey) return "";
        const key = provinceKey.toLowerCase().trim();
        if (provinceTranslations[key]) {
            return t(provinceTranslations[key]);
        }
        return key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    };

    const formatDate = (dateString) => {
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleString('en-US', options);
    };

    const handleShowDetails = (order) => {
        setSelectedOrder(order);
        setShowDetailsModal(true);
    };

    // Helper function to get capacity display name
    const getCapacityDisplayName = (product) => {
        // Check for selectedCapacity
        if (product.selectedCapacity) {
            if (typeof product.selectedCapacity === 'object') {
                return product.selectedCapacity.name || product.selectedCapacity.capacity || null;
            }
            return product.selectedCapacity;
        }

        // Check for capacity in product
        if (product.capacity) {
            return product.capacity;
        }

        // Check for capacityName
        if (product.capacityName) {
            return product.capacityName;
        }

        return null;
    };

    // Helper function to get bottle size display
    const getBottleSizeDisplay = (product) => {
        if (product.selectedBottleVariant) {
            if (typeof product.selectedBottleVariant === 'object') {
                return product.selectedBottleVariant.bottleSize || null;
            }
            return product.selectedBottleVariant;
        }
        if (product.bottleSize) {
            return product.bottleSize;
        }
        return null;
    };

    // Function to get product variant display name
    const getProductVariantDisplay = (product) => {
        const capacity = getCapacityDisplayName(product);
        const bottleSize = getBottleSizeDisplay(product);

        if (capacity && bottleSize) {
            return `${capacity} • ${bottleSize}ml`;
        }
        if (capacity) {
            return capacity;
        }
        if (bottleSize) {
            return `${bottleSize}ml`;
        }
        return null;
    };

    // Function to get product badge color
    const getVariantBadgeColor = (variant) => {
        if (!variant) return 'secondary';
        if (variant.toLowerCase().includes('full box')) return 'success';
        if (variant.toLowerCase().includes('ml')) return 'info';
        return 'secondary';
    };

    const filteredOrders = filterStatus === 'all'
        ? orders
        : orders.filter(order => order.status === filterStatus);

    if (loading) {
        return (
            <Container className="order-history-container">
                <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading your orders...</p>
                </div>
            </Container>
        );
    }

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        const digits = phone.replace(/\D/g, '');
        return digits.replace(/(\d{3})(\d{3})(\d{3,4})/, '$1 $2 $3');
    };

    const handleImageClick = (imageUrl, productName) => {
        setSelectedImage(imageUrl);
        setSelectedImageAlt(productName);
        setShowImageModal(true);
    };

    return (
        <Container className="order-history-container" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
            {/* Header Section */}
            <div className="order-history-header text-center mb-4 mb-md-4">
                <h1 className="fs-2 fw-bold text-dark mb-3">
                    <FaShoppingBag className="me-3" />
                    {t('Your Order History')}
                </h1>
                <p className="text-muted lead">
                    {t("Track and view your all orders")}
                </p>
            </div>

            {error && (
                <Alert variant="warning" className="text-center mb-4">
                    {error}
                </Alert>
            )}

            {/* Filter Section */}
            <Card className="filter-card mb-3">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={6}>
                            <h5 className="mb-4 mb-md-0">
                                {t("# Order Total")}: {filteredOrders.length}
                            </h5>
                        </Col>
                        <Col md={6}>
                            <Form.Select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="status-filter"
                            >
                                <option value="all">{t("All Orders")}</option>
                                <option value="Pending">{t("Pending")}</option>
                                <option value="Shipped">{t("On Delivery")}</option>
                            </Form.Select>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Orders List */}
            {filteredOrders.length === 0 ? (
                <Card className="text-center empty-state-card">
                    <Card.Body className="py-5">
                        <div className="empty-state-icon mb-3">
                            <FaShoppingBag size={64} className="text-muted" />
                        </div>
                        <h4 className="text-muted">{t("No orders found")}</h4>
                        <p className="text-muted">
                            {filterStatus === 'all'
                                ? t("No orders have been placed yet.")
                                : t(`No ${filterStatus.toLowerCase()} orders found.`)
                            }
                        </p>
                        <Button variant="primary" href="/shop">
                            {t("Start Shopping")}
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <div className="orders-list">
                    {filteredOrders.map((order, index) => (
                        <Card key={order._id} className="order-card mb-3">
                            <Card.Body>
                                {/* Order Header */}
                                <Row className="align-items-center mb-3">
                                    <Col xs={8} className="text-start">
                                        <div className="order-header-info">
                                            <h5 className="order-id mb-1">Order #{order.orderId}</h5>
                                            <small className="text-muted">
                                                <FaCalendarAlt className="me-1" />
                                                {formatDate(order.createdAt)}
                                            </small>
                                        </div>
                                    </Col>

                                    <Col xs={4} className="text-end">
                                        <div className="d-inline-block">
                                            <Badge bg={getStatusVariant(order.status)} className="status-badge mb-1 py-2 fw-bold">
                                                {getStatusText(order.status)}
                                            </Badge>
                                            <div className="mt-1">
                                                <strong className="total-amount">${order.totalAmount?.toFixed(2)}</strong>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                {/* Order Summary */}
                                <Row className="order-summary align-items-center">
                                    <Col xs={8} className="text-start">
                                        <div className="products-preview">
                                            {order.products?.slice(0, 3).map((product, idx) => (
                                                <span key={idx} className="product-name">
                                                    {product.productName}
                                                    {product.quantity > 1 && ` (x${product.quantity})`}
                                                    {idx < Math.min(order.products.length - 1, 2) && ', '}
                                                </span>
                                            ))}
                                            {order.products?.length > 3 && (
                                                <span className="text-muted"> and {order.products.length - 3} more items</span>
                                            )}
                                        </div>
                                        <div className="customer-info mt-2">
                                            <small className="text-muted">
                                                <FaMapMarkerAlt className="me-1" />
                                                {formatProvince(order.province)} •
                                                <FaPhone className="ms-2 me-1" />
                                                {formatPhoneNumber(order.customerPhone)}
                                            </small>
                                        </div>
                                    </Col>

                                    <Col xs={4} className="text-end">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleShowDetails(order)}
                                            className="details-btn"
                                        >
                                            <FaEye className="me-2" />
                                            {t("Details")}
                                        </Button>
                                    </Col>
                                </Row>

                            </Card.Body>
                        </Card>
                    ))}
                </div>
            )}

            {/* Order Details Modal */}
            <Modal
                show={showDetailsModal}
                onHide={() => setShowDetailsModal(false)}
                size="lg"
                centered
                style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t("Order Details")} #{selectedOrder?.orderId}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedOrder && (
                        <>
                            {/* Order Status and Summary */}
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="order-status-section">
                                        <div className='d-flex align-items-center justify-content-between mt-2'>
                                            <h6>{t("Order Status")}</h6>
                                            <Badge
                                                bg={getStatusVariant(selectedOrder.status)}
                                                className="fs-6"
                                            >
                                                {getStatusText(selectedOrder.status)}
                                            </Badge>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <h6 className="mb-0">
                                                {t("Ordered Date")}:
                                            </h6>
                                            <h6>{formatDate(selectedOrder.createdAt)}</h6>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="order-total-section d-flex align-items-center justify-content-between mt-3 text-end">
                                        <h6>{t("Total Amount")}</h6>
                                        <h4 className="text-primary">
                                            <FaDollarSign />
                                            {selectedOrder.totalAmount?.toFixed(2)}
                                        </h4>
                                        {selectedOrder.isFreeDelivery && (
                                            <Badge bg="success" className="mt-1">
                                                🎉 {t("Free Delivery Applied")}
                                            </Badge>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {/* Customer Information */}
                            <Card className="mb-4">
                                <Card.Header>
                                    <h6 className="mb-0">{t("Customer Information")}</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <p><strong>{t("Name")}:</strong> {selectedOrder.customerName}</p>
                                            <p><strong>{t("Phone")}:</strong> {formatPhoneNumber(selectedOrder.customerPhone)}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>{t("Province")}:</strong> {formatProvince(selectedOrder.province)}</p>
                                            <p><strong>{t("Address")}:</strong> {selectedOrder.address}</p>
                                            <p><strong>{t("Delivery Method")}:</strong> {translateDeliveryMethod(selectedOrder.deliveryMethod)}</p>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Products List */}
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">Order Items</h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive className="mb-0">
                                        <thead className="table-light order-history-table-header">
                                            <tr>
                                                <th>{t("Product")}</th>
                                                <th>{t("Variant")}</th>
                                                <th>{t("Quantity")}</th>
                                                <th>{t("Unit Price")}</th>
                                                <th>{t("Discount")}</th>
                                                <th>{t("Total")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedOrder.products?.map((product, index) => {
                                                const originalPrice = product.discount > 0
                                                    ? product.unitPrice / (1 - product.discount / 100)
                                                    : product.unitPrice;

                                                const variantDisplay = getProductVariantDisplay(product);

                                                return (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="product-info d-flex align-items-center">
                                                                {/* Product Image */}
                                                                {(product.productImage || product.image || product.images?.[0]) && (
                                                                    <div
                                                                        className="product-image me-3"
                                                                        style={{ width: '60px', height: '60px', cursor: 'pointer' }}
                                                                        onClick={() =>
                                                                            handleImageClick(
                                                                                product.productImage || product.image || product.images?.[0],
                                                                                product.productName
                                                                            )
                                                                        }
                                                                    >
                                                                        <img
                                                                            src={product.productImage || product.image || product.images?.[0]}
                                                                            alt={product.productName}
                                                                            className="img-fluid rounded border"
                                                                            style={{
                                                                                width: '100%',
                                                                                height: '100%',
                                                                                objectFit: 'contain'
                                                                            }}
                                                                            onError={(e) => {
                                                                                e.target.onerror = null;
                                                                                e.target.src = 'https://via.placeholder.com/60x60?text=No+Image';
                                                                            }}
                                                                            title="Click to view larger image"
                                                                        />
                                                                    </div>
                                                                )}

                                                                <div>
                                                                    <strong className='order-history-product-name'>
                                                                        {product.productName}
                                                                        {product.selectedColor && (
                                                                            <span className="ms-1 text-muted">
                                                                                <span>
                                                                                    (
                                                                                    {product.selectedColor.hexCode && (
                                                                                        <span
                                                                                            className="me-1 d-inline-block align-middle"
                                                                                            style={{
                                                                                                display: 'inline-block',
                                                                                                width: '10px',
                                                                                                height: '10px',
                                                                                                backgroundColor: product.selectedColor.hexCode,
                                                                                                border: '1px solid #ccc',
                                                                                                borderRadius: '50%',
                                                                                                verticalAlign: 'middle'
                                                                                            }}
                                                                                            title={`Color: ${product.selectedColor.name}`}
                                                                                        />
                                                                                    )}
                                                                                    {product.selectedColor.name})
                                                                                </span>
                                                                            </span>
                                                                        )}
                                                                    </strong>
                                                                    {product.selectedOptions && (
                                                                        <div className="text-muted small">
                                                                            {Object.entries(product.selectedOptions).map(([key, value]) => (
                                                                                <div key={key}>
                                                                                    {key}: {value}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                            </div>
                                                        </td>
                                                        <td>
                                                            {variantDisplay ? (
                                                                <Badge
                                                                    bg={getVariantBadgeColor(variantDisplay)}
                                                                    className="p-2"
                                                                    style={{ fontSize: '0.75rem' }}
                                                                >
                                                                    <FaBox className="me-1" />
                                                                    {variantDisplay}
                                                                </Badge>
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>
                                                        <td>{product.quantity}</td>
                                                        <td>${originalPrice.toFixed(2)}</td>
                                                        <td>
                                                            {product.discount > 0 ? (
                                                                <Badge bg="warning" text="dark">
                                                                    {product.discount}% off
                                                                </Badge>
                                                            ) : (
                                                                '-'
                                                            )}
                                                        </td>
                                                        <td>${product.totalPrice.toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot className="table-light">
                                            {/* Products Subtotal */}
                                            <tr>
                                                <td colSpan="5" className="text-end">
                                                    <strong>{t("Products Subtotal")}:</strong>
                                                </td>
                                                <td>
                                                    <strong>${(selectedOrder.totalAmount - (selectedOrder.deliveryFee || 0)).toFixed(2)}</strong>
                                                </td>
                                            </tr>

                                            {/* Delivery Fee */}
                                            <tr>
                                                <td colSpan="5" className="text-end">
                                                    <div>
                                                        <strong>{t("Delivery Fee")}:</strong>
                                                        {selectedOrder.isFreeDelivery && (
                                                            <Badge bg="success" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                                                {t("FREE")}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <strong>
                                                        {selectedOrder.isFreeDelivery ? (
                                                            <span className="text-success text-decoration-line-through">
                                                                ${(selectedOrder.deliveryFee || 0).toFixed(2)}
                                                            </span>
                                                        ) : (
                                                            `$${(selectedOrder.deliveryFee || 0).toFixed(2)}`
                                                        )}
                                                    </strong>
                                                </td>
                                            </tr>

                                            {/* Final Total */}
                                            <tr className="table-primary">
                                                <td colSpan="5" className="text-end">
                                                    <strong className="fs-6">{t("Total Amount")}:</strong>
                                                </td>
                                                <td>
                                                    <strong className="fs-6">${selectedOrder.totalAmount?.toFixed(2)}</strong>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDetailsModal(false)}
                    >
                        {t("Close")}
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Image Modal for Full Size View */}
            <Modal
                show={showImageModal}
                onHide={() => setShowImageModal(false)}
                size="md"
                centered
                dialogClassName="image-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Product Image</Modal.Title>
                </Modal.Header>
                <Modal.Body
                    className="text-center p-4"
                    style={{
                        minHeight: '400px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <div className="image-container">
                        <img
                            src={selectedImage}
                            alt={selectedImageAlt}
                            className="img-fluid rounded"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '60vh',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                border: '4px solid white'
                            }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/500x500?text=Image+Not+Available';
                            }}
                        />
                    </div>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default OrderHistory;

// Correct with 703 line code changes
